import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import { alpha } from '@mui/material/styles';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import Iconify from '../../theme/iconify/iconify';

import Customisation from './Customisation';

function AccountPopover({ user, updateUser }) {
    const [open, setOpen] = React.useState(null);
    const [customise, setCustomise] = React.useState(false);

    const { t } = useTranslation();
    const navigate = useNavigate();

    const logout = () => {
        handleClose();
        sessionStorage.removeItem('mISLec_access_token');
        if (sessionStorage.getItem('mISLec_landing_chat')) sessionStorage.removeItem('mISLec_landing_chat');
        updateUser(null);
    }

    const handleOpen = (event) => {
        setOpen(event.currentTarget);
    };

    const handleClose = () => {
        setOpen(null);
    };

    return (
        <>
            <IconButton
                onClick={handleOpen}
                sx={{
                    width: 40,
                    height: 40,
                    background: (theme) => alpha(theme.palette.grey[500], 0.08),
                    ...(open && {
                        background: (theme) =>
                            `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                    }),
                }}
            >
                <Avatar
                    src='/assets/icons/user.png'
                    alt={`${user.fname} ${user.lname}`}
                    sx={{
                        width: 36,
                        height: 36,
                        border: (theme) => `solid 2px ${theme.palette.background.default}`,
                    }}
                >
                    {`${user.fname} ${user.lname}`}
                </Avatar>
            </IconButton>
            <Popover
                open={!!open}
                anchorEl={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    sx: {
                        p: 0,
                        mt: 1,
                        ml: 0.75,
                        width: 200,
                    },
                }}
            >
                <Box sx={{ my: 1.5, px: 2 }}>
                    <Typography variant='subtitle2' noWrap>
                        {`${user.fname} ${user.lname}`}
                    </Typography>
                    <Typography variant='body2' sx={{ color: 'text.secondary' }} noWrap>
                        {user.email}
                    </Typography>
                </Box>
                <Divider sx={{ mx: 1.5 }} />
                <MenuItem
                    key='profile'
                    onClick={() => {
                        handleClose();
                        navigate('/profile');
                    }}
                    sx={{ my: 0.5, px: 2 }}
                >
                    <ListItemIcon sx={{ minWidth: '20px !important', mr: 1 }}>
                        <Iconify icon='mdi:user-outline' />
                    </ListItemIcon>
                    <ListItemText sx={{ fontSize: '0.85rem' }}>
                        {t('header.account.profile')}
                    </ListItemText>
                </MenuItem>
                {user.role === 'admin' &&
                    <>
                        <MenuItem
                            key='customisation'
                            onClick={() => {
                                handleClose();
                                setCustomise(true);
                            }}
                            sx={{ my: 0.5, px: 2 }}
                        >
                            <ListItemIcon sx={{ minWidth: '20px !important', mr: 1 }}>
                                <Iconify icon='bi:lightning-charge' />
                            </ListItemIcon>
                            <ListItemText sx={{ fontSize: '0.85rem' }}>
                                {t('header.account.customise')}
                            </ListItemText>
                        </MenuItem>
                        <Divider sx={{ mx: 1.5, my: '0 !important' }} />
                    </>
                }
                <MenuItem
                    disableRipple
                    disableTouchRipple
                    onClick={() => logout()}
                    sx={{ typography: 'body2', color: 'error.main', my: 0.5, px: 2 }}
                >
                    <ListItemIcon sx={{ color: 'error.main', minWidth: '20px !important', mr: 1 }}>
                        <Iconify icon='mdi:logout' />
                    </ListItemIcon>
                    <ListItemText sx={{ fontSize: '0.85rem' }}>
                        {t('header.account.logout')}
                    </ListItemText>
                </MenuItem>
            </Popover>
            <Customisation open={customise} onClose={() => setCustomise(false)} user={user} updateUser={updateUser} />
        </>
    );
}

export default AccountPopover;